import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, Print as PrintIcon } from '@mui/icons-material';
import { printClient } from '../../utils/printClient';
import { readStoredPrinter } from '../../utils/printerState.mjs';
import { useIsMobile } from '../../utils/responsive';

function formatWhen(value) {
  if (!value) return '';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function labelTitle(item) {
  const name = String(item?.product_name || '').trim() || 'Этикетка';
  const color = String(item?.color_label || '').trim();
  return color ? `${name} (${color})` : name;
}

export default function RecentProductLabels({ open, onOpenChange, refreshKey, onPrinted, onError }) {
  const isMobile = useIsMobile();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reprintingId, setReprintingId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await printClient.listRecentProductLabels());
    } catch (error) {
      setItems([]);
      onError?.(error?.response?.data?.message || 'Не удалось загрузить этикетки');
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    if (!open) return undefined;
    load();
    return undefined;
  }, [open, refreshKey, load]);

  const reprint = async (item) => {
    const stored = readStoredPrinter();
    if (!stored?.printer_uid) {
      onError?.('Выберите принтер в Настройки → Печать');
      return;
    }
    setReprintingId(String(item.id));
    try {
      await printClient.reprintProductLabel(item.id, stored.printer_uid);
      onPrinted?.();
      await load();
    } catch (error) {
      onError?.(error?.response?.data?.message || 'Не удалось перепечатать');
    } finally {
      setReprintingId('');
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        right: { xs: 16, sm: 24 },
        bottom: isMobile ? 80 : 24,
        zIndex: 1090,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 1,
        maxWidth: 'calc(100vw - 32px)',
      }}
    >
      {open && (
        <Paper
          elevation={3}
          sx={{
            width: { xs: 'min(100vw - 32px, 360px)', sm: 360 },
            maxHeight: 'min(50vh, 420px)',
            overflow: 'auto',
            p: 1.5,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Последние этикетки
          </Typography>
          {loading && !items.length ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : null}
          {!loading && !items.length ? (
            <Typography variant="body2" color="text.secondary">
              Вы ещё не печатали этикетки
            </Typography>
          ) : null}
          {items.map((item) => (
            <Box
              key={item.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.75,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                  {labelTitle(item)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[item.copies > 1 ? `${item.copies} шт` : '', formatWhen(item.created_at)].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={String(reprintingId) === String(item.id) ? <CircularProgress size={14} /> : <PrintIcon />}
                disabled={!item.content_available || Boolean(reprintingId)}
                onClick={() => reprint(item)}
              >
                Ещё раз
              </Button>
            </Box>
          ))}
        </Paper>
      )}
      <IconButton
        aria-label={open ? 'Скрыть последние этикетки' : 'Последние этикетки'}
        onClick={() => onOpenChange(!open)}
        sx={{
          width: 56,
          height: 56,
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {open ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
      </IconButton>
    </Box>
  );
}
